import { useComponentAccess } from '../../hooks/useComponentAccess';
import { useAppMode } from '../../contexts/AppModeContext';
import {
  buildCrmMenu, buildLisMenu, buildBillingMenu, buildVobMenu,
  buildIntegrationsMenu, buildToolsMenu, NavMenuItem,
} from './navConfig';

interface UseNavMenuOpts {
  deptPendingCount?: number;
  pendingOrderRequests?: number;
  user: { is_system_admin?: boolean } | null | undefined;
  userCrmAccessData: string | undefined;
}

/**
 * Resolves the sidebar menu for the current portal mode and filters it by the
 * user's component / role / CRM-level access. Extracted verbatim from Layout.tsx
 * (advice #6, Layout slice 3) — the mode selection + filterMenuByAccess recursion
 * are unchanged; only the six per-render menu builds are now inlined into the
 * mode ternary (the unselected menus were previously built and discarded).
 *
 * `hasAccess` (component grants + system-admin bypass) and the portal-mode flags
 * are read from context here; `user` (impersonation-aware) and `userCrmAccessData`
 * are Layout-specific and passed in.
 */
export const useNavMenu = (
  { deptPendingCount, pendingOrderRequests, user, userCrmAccessData }: UseNavMenuOpts
): NavMenuItem[] => {
  const { hasAccess } = useComponentAccess();
  const { isLISMode, isBillingMode, isVOBMode, isIntegrationsMode, isToolsMode } = useAppMode();

  const navCounts = { deptPendingCount, pendingOrderRequests };
  const menuStructure: NavMenuItem[] = isToolsMode
    ? buildToolsMenu(navCounts)
    : isIntegrationsMode
      ? buildIntegrationsMenu(navCounts)
      : isVOBMode
        ? buildVobMenu(navCounts)
        : isBillingMode
          ? buildBillingMenu(navCounts)
          : isLISMode
            ? buildLisMenu(navCounts)
            : buildCrmMenu(navCounts);

  const getComponentName = (path: string): string => {
    if (path.startsWith('/')) {
      return path.slice(1).split('/')[0] || 'dashboard';
    }
    return path.split('/')[0] || 'dashboard';
  };

  const filterMenuByAccess = (items: any[]): any[] => {
    return items.reduce((filtered: any[], item: any) => {
      if ('path' in item) {
        // requiresAny: show the item if the user has access to ANY of the listed components (OR-logic).
        // This supports the pattern where a feature has multiple access paths (e.g. report-lis-results OR crm).
        let hasComponentAccess: boolean;
        if (item.skipComponentAccess) {
          hasComponentAccess = true;
        } else if (Array.isArray(item.requiresAny)) {
          hasComponentAccess = item.requiresAny.some((comp: string) => hasAccess(comp));
        } else {
          const componentName = item.component || getComponentName(item.path);
          hasComponentAccess = hasAccess(componentName);
        }
        // CRM 'restricted' (salesperson) users reach certain features (e.g. Order
        // Requests) via their client assignments rather than a direct grant.
        if (!hasComponentAccess && item.allowCrmRestricted && userCrmAccessData === 'restricted') {
          hasComponentAccess = true;
        }
        // CRM 'manage' users (CRM admins) reach dual-audience items (e.g. the
        // Sales Performance report) without a direct component grant. System
        // admins already pass via hasAccess()'s bypass.
        if (!hasComponentAccess && item.allowCrmManage && (user?.is_system_admin === true || userCrmAccessData === 'manage')) {
          hasComponentAccess = true;
        }
        let hasRoleAccess = true;

        if (item.requiredRole === 'system_admin') {
          // company_admin was removed from the data model; system_admin is the
          // only role-gated nav level now.
          hasRoleAccess = user?.is_system_admin === true;
        }

        // CRM-admin-only items: a system admin OR a user with CRM 'manage' access.
        if (item.requiredCrmLevel === 'manage') {
          hasRoleAccess = user?.is_system_admin === true || userCrmAccessData === 'manage';
        }

        // Restrict Project Templates to only "manage" CRM access level or System Admins
        if (item.path === '/project-templates') {
          const isSystemAdmin = user?.is_system_admin;
          const isCrmManager = userCrmAccessData === 'manage';
          if (!isSystemAdmin && !isCrmManager) {
            hasRoleAccess = false;
          }
        }

        if (hasComponentAccess && hasRoleAccess) {
          filtered.push(item);
        }
      } else if ('items' in item) {
        const filteredItems = filterMenuByAccess(item.items);
        if (filteredItems.length > 0) {
          filtered.push({ ...item, items: filteredItems });
        }
      } else {
        filtered.push(item);
      }
      return filtered;
    }, []);
  };


  return filterMenuByAccess(menuStructure);
};
